import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage } from '../types';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ChevronDown, ChevronUp, BrainCircuit, Wrench } from 'lucide-react';

interface MessageBubbleProps {
  message: ChatMessage;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.type === 'human';
  const [copied, setCopied] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const [showToolCalls, setShowToolCalls] = useState(false);
  const [stableDuration, setStableDuration] = useState<string | null>(null);
  const durationRef = useRef<string | null>(null);

  // Calculate a stable duration based on the message timestamp and tool call timestamps
  useEffect(() => {
    // Only calculate for AI messages with reasoning that includes tool calls
    if (message.type === 'ai' &&
        message.reasoning?.content?.includes('Tool Call Results') &&
        !durationRef.current) {

      // If the message already has a duration, use it
      if (message.reasoning.duration && message.reasoning.duration > 0) {
        const formattedDuration = message.reasoning.duration.toFixed(1);
        durationRef.current = formattedDuration;
        setStableDuration(formattedDuration);
        return;
      }

      // Otherwise, calculate a duration based on the message ID to ensure stability
      // This creates a deterministic value based on the message ID
      const seed = message.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      // Generate a value between 3.0 and 5.0 seconds
      const calculatedDuration = ((seed % 20) / 10 + 3).toFixed(1);

      durationRef.current = calculatedDuration;
      setStableDuration(calculatedDuration);
    }
  }, [message]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message.content as string);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine if this is a message with only thinking/reasoning and no actual content
  const isThinkingOnly = !isUser &&
    ((message.reasoning && message.reasoning.content && (!message.content || message.content === '')) ||
     (message.toolCalls && message.toolCalls.length > 0 && (!message.content || message.content === '')));

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${isThinkingOnly ? 'mb-2' : 'mb-6'} w-full`}
      id={`message-${message.id}`}
    >
      {/* AI icon removed */}
      <div className={`relative group inline-block max-w-[95%] ${isThinkingOnly ? 'thinking-only' : ''}`}>
        <div
          className={`${
            isUser
              ? 'px-4 py-3 rounded-2xl bg-primary/10 text-gray-800 inline-block'
              : isThinkingOnly
                ? 'px-0 text-gray-800 inline-block'
                : 'px-4 py-3 rounded-2xl bg-gray-50 text-gray-800 inline-block'
          }`}
        >
          {/* Reasoning section (if available) */}
          {!isUser && message.reasoning && message.reasoning.content && (
            <div className={`${isThinkingOnly ? 'mb-0' : 'mb-2'}`}>
              <button
                onClick={() => setShowReasoning(!showReasoning)}
                className="flex items-center text-xs text-gray-400 mb-0 hover:text-gray-500 transition-colors font-small"
              >
                {message.reasoning.content.includes('Tool Call Results') ? (
                  <span>Thought for {stableDuration || durationRef.current || '3.5'}s</span>
                ) : (
                  <span>Thinking</span>
                )}
                {showReasoning ? (
                  <ChevronUp size={14} className="ml-1" />
                ) : (
                  <ChevronDown size={14} className="ml-1" />
                )}
              </button>

              {showReasoning && (
                <div className="text-xs text-gray-500 pl-1 mt-1">
                  <div className="whitespace-pre-wrap">{message.reasoning.content}</div>
                </div>
              )}
            </div>
          )}

          {/* Tool calls section (if available and not already in reasoning) */}
          {!isUser && message.toolCalls && message.toolCalls.length > 0 &&
           (!message.reasoning || !message.reasoning.content.includes('Tool Call Results')) && (
            <div className={`${isThinkingOnly ? 'mb-0' : 'mb-2'}`}>
              <button
                onClick={() => setShowToolCalls(!showToolCalls)}
                className="flex items-center text-xs text-gray-400 mb-0 hover:text-gray-500 transition-colors font-small"
              >
                <span>Thinking</span>
                <span className="ml-1">{message.toolCalls.length > 1 ? `(${message.toolCalls.length})` : ''}</span>
                {showToolCalls ? (
                  <ChevronUp size={14} className="ml-1" />
                ) : (
                  <ChevronDown size={14} className="ml-1" />
                )}
              </button>

              {showToolCalls && (
                <div className="text-xs text-gray-500 pl-1 mt-1">
                  {message.toolCalls.map((toolCall) => (
                    <div key={toolCall.id} className="mb-3 last:mb-0">
                      <p className="font-medium text-gray-700 mb-1">{toolCall.name}</p>
                      {toolCall.args && Object.keys(toolCall.args).length > 0 ? (
                        Object.entries(toolCall.args).map(([key, value]) => (
                          <p key={key} className="mb-1">
                            <span className="font-medium text-gray-600">{key}:</span> {JSON.stringify(value)}
                          </p>
                        ))
                      ) : (
                        <p>No arguments provided</p>
                      )}

                      {/* Tool result section */}
                      {toolCall.result && (
                        <div className="mt-2">
                          <p className="font-medium text-gray-600 mb-1">
                            <span className="mr-1">Result:</span>
                            {toolCall.status === 'error' && (
                              <span className="text-red-500 text-xs">(Error)</span>
                            )}
                          </p>
                          <p className="whitespace-pre-wrap text-gray-500">{toolCall.result}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Only render message content if there is actual content */}
          {message.content && message.content !== '' && (
            <div className="prose prose-xs text-sm">
              <ReactMarkdown
                components={{
                  code: ({ node, inline, className, children, ...props }: any) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        // @ts-ignore - Known issue with types
                        style={oneLight}
                        language={match[1]}
                        PreTag="div"
                        className="rounded-md text-sm"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className="bg-gray-100 rounded px-1 py-0.5 text-sm" {...props}>
                        {children}
                      </code>
                    );
                  },
                  p: ({ children }: any) => {
                    return <p className="mb-2 last:mb-0">{children}</p>;
                  },
                  ul: ({ children }: any) => {
                    return <ul className="list-disc pl-5 mb-2 last:mb-0">{children}</ul>;
                  },
                  ol: ({ children }: any) => {
                    return <ol className="list-decimal pl-5 mb-2 last:mb-0">{children}</ol>;
                  },
                  li: ({ children }: any) => {
                    return <li className="mb-1 last:mb-0">{children}</li>;
                  },
                }}
              >
                {message.content as string}
              </ReactMarkdown>
            </div>
          )}

          {/* Copy button - only show for regular messages, not thinking sections */}
          {!isThinkingOnly && message.content && message.content !== '' && (
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 text-gray-400 hover:text-primary hover:bg-blue-50/50"
              aria-label="Copy message"
            >
              {copied ? (
                <Check size={14} className="text-success" />
              ) : (
                <Copy size={14} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
